import React, { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import {
  FilterSectionStyles,
  SortDropdown,
} from "../../../styled-components/FilterStyles";
import { StyledHeader } from "../../ClubDetail/ClubFinancial/ClubFinancial";
import LabelText from "../../../ui/LabelText";
import SecondaryButton from "../../../ui/SecondaryButton";
import playerFilterData from "../../../data/playerFilterData";
import FilterSort from "./FilterSort";
import PlayerInfo from "../../PlayerInfo";
import {
  IAgeStatistics,
  IPlayerCardData,
  ISimilarPlayerCardDetail,
} from "../../../types/IPlayerDetail";
import PaginationComponent from "../../../ui/PaginationComponent";
import PlayerFilterSection, {
  PlayerFilterSectionRef,
} from "./PlayerFilterSection";
import { usePlayerSearch } from "../../../contexts/SearchPlayersContext";
import { useLoading } from "../../../contexts/LoadingContext";
import IApiResponse from "../../../types/IApiResponse";
import apiUrls from "../../../config/apiUrls";
import handleApiResponse from "../../../utils/handleApiResponse";
import { postData } from "../../../services/api";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PWNotification from "../../PWNotification";
import { useNotification } from "../../../contexts/NotificationContext";
import { IPlayerSearchResponse } from "../../../types/IPlayerSearchResponse";
import SelectAttributes, { IAttribute } from "./SelectAttributes";
import SelectTags, { ITag } from "./SelectTags";
import { useNavigate } from "react-router-dom";
import MachineFootballIcon from '../../../assets/sidebarIcons/MachineFootballIcon.png';
import { styled } from '@mui/material/styles';
import LinearProgress, { linearProgressClasses } from '@mui/material/LinearProgress';
import LoadingSpinner from "../../../ui/LoadingSpinner";
import { useSearch } from "../../../contexts/PlayerSearchContext";
import { filterWomensFootball } from "../../../utils/womensFootballFilter";


interface IPlayerFilterResultsPage {
  selectedSimilarPlayer?: IPlayerSearchResponse[];
  onClearPlayerListClick?: () => void;
  club?: number;
  isFromSuccession?: boolean;
}

type SortType = {
  filter: string;
  order: string;
};

export const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 10,
  borderRadius: 5,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: theme.palette.grey[300],
    ...theme.applyStyles('dark', {
      backgroundColor: theme.palette.grey[800],
    }),
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 5,
    backgroundColor: '#00D1A8',
    ...theme.applyStyles('dark', {
      backgroundColor: '#308fe8',
    }),
  },
}));

const PlayerFilterResultsPage: React.FC<IPlayerFilterResultsPage> = ({
  selectedSimilarPlayer,
  onClearPlayerListClick,
  club,
  isFromSuccession
}) => {
  const {
    playerSearchResultsVisible,
    selectedPlayers,
    selectedAttributesState,
    selectedProfiles,
    foot,
    ageRange,
    transferValue,
    trueValue,
    contractUntilValue,
    regionValues,
    competitionValues,
    minAppearances,
    startsOnly,
    resultSortOrderName,
    resultSortOrdering,
    isSearchByAttributes,
    isAllPosition,
    selectedTags,
    isExactMatchesOnly,
    handleSelectedPlayerZones,
    handleSelectedProfiles,
    setAllProfiles,
    setPlayerSearchResultsVisible,
    setSimilarPlayerResultsList,
    originPage,
    isPlayerFilterChange,
    setIsPlayerFilterChange,
    similarPlayerResultsList,
    currentPlayerPage,
    setCurrentPlayerPage,
    attributesDrawerOpen,
    setAttributesDrawerOpen,
    tagsDrawerOpen,
    setTagsDrawerOpen,
    toggleAddButton,
    setIsToggleAddButton,
    isAffordabilityFilter, setIsAffordabilityFilter,
    isStylisticFilter, setIsStylisticFilter
  } = usePlayerSearch();
  const {
    showNotification,
    hideNotification,
    displayNotification,
    setDisplayNotification,
    notificationContent,
    setNotificationContent,
  } = useNotification();

      const {selectedPositionIds}= useSearch();
      const { loading, startLoading, stopLoading } = useLoading();
      const navigate = useNavigate();
      const [isSortClicked, setIsSortClicked] = useState(false);
      const [playerResults, setPlayerResult] = useState<IPlayerCardData[]>(
        similarPlayerResultsList?.playerDetailCardModels ?? []
      );
      const [openIndex, setOpenIndex] = useState(0);
      const [playerResultsCount, setPlayerResultsCount] = useState(0);
      const [selectedAttributes, setSelectedAttributes] = useState<IAttribute[]>(
        []
      );
      const [selectedPlayerTags, setSelectedPlayerTags] = useState<ITag[]>([]);
      const [ageStatistics, setAgeStatistics] = useState<IAgeStatistics>({
        minAge: 0,
        maxAge: 0,
        ageDistribution: [],
      });
      const defaultSort: SortType = { filter: "Similarity", order: "DESC" };
      const playerFilterSectionRef = useRef<PlayerFilterSectionRef>(null);
      const filterSortRef = useRef<HTMLDivElement | null>(null);
      const [playerIds, setPlayerIds] = useState<number[]>([]);
      const [additionalData, setAdditionalData] = useState<string[]>([]);
      const positionMap: Record<number, string[] | string> = {
      1: ["cf"],
      2: ["lw", "lwf"],
      3: ["rw", "rwf"],
      4: ["lamf", "ramf", "amf"],
      5: ["lcmf", "rcmf", "lcmf3", "rcmf3"],
      6: ["ldmf", "rdmf", "dmf"],
      7: ["lcb", "lcb3", "cb"],
      8: ["rcb", "rcb3", "cb"],
      9: ["lb", "lwb", "lb5"],
      10: ["rb", "rwb", "rb5"],
      11: ["gk"]
    };
    const selectedPositions = selectedPositionIds.flatMap(code => positionMap[code]).filter(pos => pos !== undefined);

  const totalPages = Math.ceil(playerResultsCount / 20);

  useEffect(() => {
    setSelectedAttributes(selectedAttributesState);
  }, [selectedAttributesState]);

  useEffect(() => {
    setSelectedPlayerTags(selectedTags);
  }, [selectedTags]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        filterSortRef.current &&
        !filterSortRef.current.contains(event.target as Node)
      ) {
        setIsSortClicked(false); // Close FilterSort if clicked outside
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [filterSortRef]);

  const fetchPlayerSearchResults = async () => {
    try {
      startLoading();
      setIsPlayerResultsLoading(true);
      const playerIdsOnly: number[] = selectedPlayers.map(
        (response) => response.playerId
      );
      const selectedAttributeList = selectedAttributesState.map((attr) => ({
        name: attr.label,
        top: attr.percentile,
        isMadeUpScore: attr.isMadeUpScore,
      }));
      const areaValues = regionValues.map((reg) => reg.optionId);
      const competitionId = competitionValues.map((reg) => reg.optionId);
      const requestParameters = {
        positionCode: selectedPositions,
        archetypeId: selectedProfiles,
        playerId: selectedPlayers.length > 0 ? playerIdsOnly : [],
        pageNo: currentPlayerPage,
        pageSize: 20,
        footFilter: foot,
        minAge: ageRange.min,
        maxAge: ageRange.max,
        minTrueValue: trueValue.min,
        maxTrueValue: trueValue.max,
        minTransferValue: transferValue.min,
        maxTransferValue: transferValue.max,
        minContractUntil: contractUntilValue.min,
        maxContractUntil: contractUntilValue.max,
        areaId: areaValues,
        competitionId: competitionId,
        minAppearances: minAppearances,
        minMinsPlayed: minAppearances,
        IsStatsOnly: startsOnly,
        OrderName: resultSortOrderName,
        Ordering: resultSortOrdering,
        isByAttributes: isSearchByAttributes,
        ...(isFromSuccession === false ? { attributesFilterByPercentage: selectedAttributeList } : {}),
        allPosition: isAllPosition === "allpositions",
        tagsFilter:
          selectedTags.length > 0
            ? selectedTags.map((tag) => tag.id.toLowerCase())
            : [],
        isExactMatchesOnly: isExactMatchesOnly,
        ...(isFromSuccession
          ? { includeAffordability: isAffordabilityFilter }
          : {}), 
        stylisticFitUplift: isStylisticFilter,
      };
      const response: IApiResponse<ISimilarPlayerCardDetail> = await postData(
        isFromSuccession ? apiUrls.GetPlayerSuccessions : apiUrls.SimilarPlayer,
        requestParameters
      );
      handleApiResponse(
        response,
        (playersSearchResults) => {
          stopLoading();
          const filteredModels = filterWomensFootball(playersSearchResults.playerDetailCardModels || []);
          if (filteredModels.length > 0) {
            setDisplayNotification(false);
            setNotificationContent(null);
            setSimilarPlayerResultsList({
              ...playersSearchResults,
              playerDetailCardModels: filteredModels
            });
            setPlayerIds([
              ...filteredModels.map(p => p.playerId),
              ...(selectedPlayers[0] ? [selectedPlayers[0].playerId] : []),
            ]);
            setPlayerResultsCount(filteredModels.length);
            setPlayerResult(filteredModels);
            setAgeStatistics(playersSearchResults.ageStatistics);
            setIsPlayerFilterChange(false);
          } else {

            setSubProcessIndex(4)
            setLoadingProgressValue(100);
            setTimeout(()=>{
              setIsPlayerResultsLoading(false);
              setDisplayNotification(true);
            },1000)
              setNotificationContent(
              <div className="notification">
                <InfoOutlinedIcon className="icon" />
                {originPage !== "Similarplayers Tab" ? (
                  <p>
                    <LabelText className="squad-chart-tooltip notification-content">
                      No results found -
                    </LabelText>
                    <LabelText className="player-given-name notification-content">
                      {" "}
                      Try lowering the attribute scores or{" "}
                    </LabelText>
                    <LabelText
                      className="player-given-name notification-content new-search-button"
                      onClick={onClearPlayerListClick}
                    >
                      start new search
                    </LabelText>
                  </p>
                ) : (
                  <p>
                    <LabelText className="squad-chart-tooltip notification-content">
                      No results found -
                    </LabelText>
                    <LabelText className="player-given-name notification-content">
                      {" "}
                      Try adjusting the filters
                    </LabelText>
                  </p>
                )}
              </div>
            );
            setPlayerResult([]);
            setPlayerResultsCount(0);
          }
        },
        (error) => {
          stopLoading();
          setIsPlayerResultsLoading(false);
          console.error("Error fetching player search result data:", error);
        }
      );
    } catch (error) {
      stopLoading();
      setIsPlayerResultsLoading(false);
      console.error("Error fetching player search result data:", error);
    } finally {
      stopLoading();
      console.error("Error fetching player search result data:");
    }
  };

  const memoizedAgeStatistics = useMemo(() => ageStatistics, [ageStatistics]);

  useEffect(() => {
    if (playerSearchResultsVisible && similarPlayerResultsList === undefined) {
      fetchPlayerSearchResults();
    } else if (isPlayerFilterChange) {
      fetchPlayerSearchResults();
    }
  }, [
    playerSearchResultsVisible,
    originPage,
    currentPlayerPage,
    resultSortOrdering,
    resultSortOrderName,
    isPlayerFilterChange,
    selectedPlayers,
    selectedAttributesState,
    selectedProfiles,
    foot,
    ageRange,
    transferValue,
    trueValue,
    contractUntilValue,
    regionValues,
    competitionValues,
    minAppearances,
    startsOnly,
    isSearchByAttributes,
    isAllPosition,
    selectedTags,
    isExactMatchesOnly,
    isAffordabilityFilter,
    isStylisticFilter
  ]);

  const handleSortChange = () => {
    setIsSortClicked(false);
  };

  const handleToggle = (index: number) => {
    setOpenIndex((prevIndex) => (prevIndex === index ? -1 : index));
  };

  const handlePageChange = (page: number) => {
    setIsPlayerFilterChange(true);
    setCurrentPlayerPage(page);
  };

  const renderPlayerResults = () => {
    return (
      <>
        {playerResults.map((playerData, index) => (
          <div className="player-card-wrapper" key={playerData.playerId}>
            <PlayerInfo
              key={playerData.playerId}
              playerData={playerData}
              isOpen={openIndex === index}
              onToggle={() => handleToggle(index)}
              replacingPlayerID={selectedPlayers[0]?.playerId}
              replacingPlayerClubImg={selectedPlayers[0]?.playerClubURL}
              club={club}
            />
          </div>
        ))}
        {playerResults.length > 0 && (
          <div className="pagination-wrapper">
            <PaginationComponent
              totalPages={totalPages}
              currentPage={currentPlayerPage}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </>
    );
  };

  const handleConfirmAttributes = (selectedData: IAttribute[]) => {
    playerFilterSectionRef.current?.handleConfirmAttributes(selectedData);
    setSelectedAttributes(selectedData);
  };

  const handleTagConfirm = (selectedData: ITag[]) => {
    playerFilterSectionRef.current?.handleTagConfirm(selectedData);
    setSelectedPlayerTags(selectedData);
  };

  const handleSelectedAttributes = (selectedData: IAttribute[]) => {
    setSelectedAttributes(selectedData);
  };

  const handleSelectedTags = (selectedTag: ITag[]) => {
    setSelectedPlayerTags(selectedTag);
  };

  const handlePlayerComparision = () => {
    localStorage.removeItem('removedIds');
    navigate(`/playercomparison`, { state: { playerIds , isFromSortList: false,benchMarkPlayerId: selectedPlayers[0]?.playerId  } }); 
  }

  const [loadingProgressValue, setLoadingProgressValue] = useState<number>(0);
  const [isPlayerResultsLoading, setIsPlayerResultsLoading] = useState<boolean>(false);
  const [subProcessIndex, setSubProcessIndex] = useState<number>(0);
  const subProcesses = ["Retrieving player metadata to create the list", "Performing vector search for similarity", "Applying filters", "Sorting the data", "Nearly complete"]
  useEffect(() => {
    if (playerResults?.length === 0) {
      setIsPlayerResultsLoading(true);
      const intervalId = setInterval(() => {
        setLoadingProgressValue(prev => {
          if (prev >= 80) {
            clearInterval(intervalId);
            return prev;
          }
          return prev + 20;
        });
        setSubProcessIndex(prev => {
          if (prev < 4) {
            return prev + 1
          } else {
            return 4
          }
        });
      }, 5000);
      return () => clearInterval(intervalId);
    } else {
      setSubProcessIndex(4)
      setLoadingProgressValue(100);
      setTimeout(() => setIsPlayerResultsLoading(false),1000)
    }
  },[playerResults])
  
  return (
    <>
      <FilterSectionStyles className="player-filters-section">
        <div className="header-content-wrapper">
          <StyledHeader className="header-content">
            <LabelText className="player-family-name">Filters</LabelText>
          </StyledHeader>
        </div>

        <PlayerFilterSection
          ageStatistics={memoizedAgeStatistics}
          ref={playerFilterSectionRef}
          onSelectedAttributes={handleSelectedAttributes}
          onSelectedTags={handleSelectedTags}
          additionalData={additionalData}
          setAdditionalData={setAdditionalData}
          isFromSuccession={isFromSuccession}
        />
      </FilterSectionStyles>
      <div className="player-results-section">
        <div className="header-content-wrapper">
          <StyledHeader className="header-content">
            <LabelText className="player-family-name">Results</LabelText>
            <div className="result-actions-btn">
              <SecondaryButton
                iconType="sort"
                label={
                  <>
                    <span className="sort-label">Sort by: </span>
                    <LabelText className="clear-labeltext">
                      {resultSortOrderName}
                    </LabelText>
                  </>
                }
                onClick={() => setIsSortClicked((prev) => !prev)}
              />
              {isSortClicked && (
                <SortDropdown ref={filterSortRef} className="newplayer-brop">
                  <FilterSort
                    onSortChange={handleSortChange}
                    data={playerFilterData}
                    sortType="NewPlayer"
                  />
                </SortDropdown>
              )}
              <div className="comparision-action-div">
                <SecondaryButton
                  iconType="playercomparison"
                  label="Comparison tools"
                  className="comparision-tools-btn"
                  onClick={handlePlayerComparision}
                />
              </div>
            </div>
          </StyledHeader>
        </div>
        {isPlayerResultsLoading && <div className="playerresults-loader">
          <div className="loader-header-container">
            <p>Creating list</p>
            <img src={MachineFootballIcon} className="mf-logo" alt="machine-football-logo" />
          </div>
          <div className="loader-progressbar-container">
            <BorderLinearProgress variant="determinate" value={loadingProgressValue} />
          </div>
          <div className="loader-subprocess">
            {subProcesses[subProcessIndex]}
            <p>
            {loadingProgressValue}%
            </p>
          </div>
        </div>}
        {renderPlayerResults()}
        {displayNotification && additionalData.length === 0 && (
          <PWNotification
            message={notificationContent}
            setIsNotification={() => setDisplayNotification(false)}
            isDisplayCloseIcon={true}
            className="player-filter-notification add-similar-notification"
          />
        )}
      </div>

      {attributesDrawerOpen && (
        <SelectAttributes
          isOpen={attributesDrawerOpen}
          onClose={() => setAttributesDrawerOpen(false)}
          onConfirm={handleConfirmAttributes}
          selectedAttributes={selectedAttributes}
        />
      )}
      {tagsDrawerOpen && (
        <SelectTags
          isOpen={tagsDrawerOpen}
          onClose={() => setTagsDrawerOpen(false)}
          onConfirm={handleTagConfirm}
          selectedTag={selectedPlayerTags}
        />
      )}
      {loading && playerResults?.length > 0 && <LoadingSpinner />}
    </>
  );
};

export default React.memo(PlayerFilterResultsPage);
